/**
 * roomEvents.ts — Factory functions for well-typed client→server events.
 *
 * These helpers centralise event construction so that call sites never
 * have to manually fill in the `type` discriminant or generate IDs.
 */

import type {
  ClientEvent,
  ClientChatEvent,
  ClientJoinEvent,
  ClientPauseEvent,
  ClientPlayEvent,
  ClientSeekEvent,
  ClientVideoChangeEvent,
} from '../../types/websocket';
import type { VideoSource } from '../../../../shared/protocol';
import type { JoinRoomOptions } from '../../types/room';

// ---------------------------------------------------------------------------
// Event factories
// ---------------------------------------------------------------------------

/**
 * Build a JOIN event from room join options.
 */
export function createJoinEvent(options: JoinRoomOptions): ClientJoinEvent {
  return {
    type: 'JOIN',
    roomId: options.roomId,
    nickname: options.nickname,
    password: options.password,
  };
}

/**
 * Build a PLAY event.
 * @param position - Current playback position in seconds.
 * @param revision - The revision number the client believes is current.
 */
export function createPlayEvent(
  position: number,
  revision: number
): ClientPlayEvent {
  return { type: 'PLAY', position, clientRevision: revision };
}

/**
 * Build a PAUSE event.
 * @param position - Current playback position in seconds.
 * @param revision - The revision number the client believes is current.
 */
export function createPauseEvent(
  position: number,
  revision: number
): ClientPauseEvent {
  return { type: 'PAUSE', position, clientRevision: revision };
}

/**
 * Build a SEEK event.
 * @param position - Target position in seconds.
 * @param revision - The revision number the client believes is current.
 */
export function createSeekEvent(
  position: number,
  revision: number
): ClientSeekEvent {
  return { type: 'SEEK', position, clientRevision: revision };
}

/**
 * Build a VIDEO_CHANGE event.
 * @param source - A source resolved by the central video detector.
 * @param revision - The revision number the client believes is current.
 */
export function createVideoChangeEvent(
  source: VideoSource,
  revision: number
): ClientVideoChangeEvent {
  return { type: 'VIDEO_CHANGE', source, clientRevision: revision };
}

export function createRotatePasswordEvent(): Extract<ClientEvent, { type: 'ROTATE_PASSWORD' }> {
  return { type: 'ROTATE_PASSWORD' };
}

/**
 * Build a CHAT_MESSAGE event with a randomly-generated idempotency ID.
 * @param message - Plain-text message.
 */
export function createChatEvent(message: string): ClientChatEvent {
  return {
    type: 'CHAT_MESSAGE',
    message,
    id: _generateMessageId(),
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Generate a random 16-character hex string for message idempotency IDs. */
function _generateMessageId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
